import * as React from 'react';
import { values } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button, CustomInput, FormGroup, Input, Label } from 'reactstrap';
import { Link } from 'react-router-dom';
import { State } from '../../redux/state';
import { Competition, Entity } from '../../userApi';
import { LProps, withLocalization } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import { makeSimpleRoutePath, Routes } from '../../utils/routerHelper';
import { isHiddenFromScoreboard } from '../../utils/competitionLabels';
import './ccsfPrelaunch.scss';
import './ucdEwcPrelaunch.scss';

interface Props {
  competition: Competition;
  residenceTeams: Entity[];
  apiBase: string;
  authOverlayOpen?: boolean;
  launched?: boolean;
  isLoggedIn?: boolean;
  hasJoined?: boolean;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  residenceTeam: string;
  wantsUpdates: boolean;
  submitting: boolean;
  success: boolean;
  error: string;
  ics: string;
}

class UcdEwcPrelaunchLanding extends React.Component<LProps<Props>, FormState> {
  public state: FormState = {
    firstName: '',
    lastName: '',
    email: '',
    residenceTeam: '',
    wantsUpdates: true,
    submitting: false,
    success: false,
    error: '',
    ics: ''
  };

  private previousTitle = '';
  private errorRef: HTMLDivElement | null = null;
  private successRef: HTMLDivElement | null = null;

  public componentDidMount() {
    this.previousTitle = document.title;
    document.title = 'Energy & Water Challenge 2027 | UC Davis';
  }

  public componentWillUnmount() {
    if (this.previousTitle) {
      document.title = this.previousTitle;
    }
  }

  public componentDidUpdate(_prevProps: LProps<Props>, prevState: FormState) {
    if (this.state.error && this.state.error !== prevState.error && this.errorRef) {
      this.errorRef.focus();
    }
    if (this.state.success && !prevState.success && this.successRef) {
      this.successRef.focus();
    }
  }

  private scrollToSignup = () => {
    const signup = document.getElementById('ucd-ewc-signup');
    if (signup) {
      signup.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private scrollToHow = () => {
    const how = document.getElementById('ucd-ewc-how');
    if (how) {
      how.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private downloadIcs = () => {
    const body = this.state.ics
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim()
      .split('\n')
      .join('\r\n') + '\r\n';
    if (!body.trim()) {
      return;
    }
    const blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'uc-davis-energy-water-challenge-2027.ics';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const { apiBase, competition } = this.props;
    const { firstName, lastName, email, residenceTeam, wantsUpdates } = this.state;
    this.setState({ submitting: true, error: '' });

    try {
      const response = await fetch(
        `${apiBase}/competitions/${competition.name}/preregister`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            department: residenceTeam,
            wantsUpdates,
            wantsLaunchReminder: true
          })
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = payload && payload[0] && payload[0].message
          ? payload[0].message
          : 'We could not complete your preregistration. Please try again.';
        this.setState({ submitting: false, error: message });
        return;
      }
      this.setState({
        submitting: false,
        success: true,
        error: '',
        ics: payload.ics || ''
      });
    } catch (_err) {
      this.setState({
        submitting: false,
        error: 'Network error. Please try again in a moment.'
      });
    }
  }

  public render() {
    const {
      t,
      competition,
      residenceTeams,
      authOverlayOpen,
      launched,
      isLoggedIn,
      hasJoined
    } = this.props;
    const {
      firstName,
      lastName,
      email,
      residenceTeam,
      wantsUpdates,
      submitting,
      success,
      error,
      ics
    } = this.state;
    const signupPath = makeSimpleRoutePath(Routes.Signup, t);
    const loginPath = makeSimpleRoutePath(Routes.Login, t);
    const joinPath = makeSimpleRoutePath(Routes.Join, t);
    const needsJoin = Boolean(isLoggedIn && !hasJoined);
    const primaryPath = needsJoin ? joinPath : signupPath;
    const primaryLabel = needsJoin ? t('Join now') : t('Create account');

    return (
      <div
        className="ccsf-prelaunch ucd-ewc-prelaunch"
        aria-hidden={authOverlayOpen ? true : undefined}
      >
        <a className="ccsf-prelaunch__skip" href="#ucd-ewc-main">
          {t('Skip to main content')}
        </a>
        <header className="ccsf-prelaunch__nav" role="banner">
          <div className="ccsf-prelaunch__nav-inner">
            <strong className="ccsf-prelaunch__logo-text">
              {t('UC Davis Energy & Water Challenge 2027')}
            </strong>
            <nav className="ccsf-prelaunch__nav-actions" aria-label={t('Page')}>
              <button
                type="button"
                className="ccsf-prelaunch__link-btn"
                onClick={this.scrollToHow}
              >
                {t('How it works')}
              </button>
              <Link className="ccsf-prelaunch__text-link" to={loginPath}>
                {t('Log in')}
              </Link>
              {launched || needsJoin ? (
                <Link to={primaryPath}>
                  <Button color="primary" className="ccsf-prelaunch__cta-sm">
                    {primaryLabel}
                  </Button>
                </Link>
              ) : (
                <Button
                  color="primary"
                  className="ccsf-prelaunch__cta-sm"
                  onClick={this.scrollToSignup}
                >
                  {t('Pre-register')}
                </Button>
              )}
            </nav>
          </div>
        </header>

        <main id="ucd-ewc-main">
          <section
            className="ccsf-prelaunch__hero ucd-ewc-prelaunch__hero"
            aria-labelledby="ucd-ewc-hero-heading"
          >
            <div className="ucd-ewc-prelaunch__hero-pattern" aria-hidden={true} />
            <div className="ccsf-prelaunch__hero-inner">
              <p className="ccsf-prelaunch__eyebrow">
                {t('UC Davis Student Housing and Dining Services')}
              </p>
              <h1 id="ucd-ewc-hero-heading">{t('Energy & Water Challenge 2027')}</h1>
              <p className="ccsf-prelaunch__badge">
                {launched ? t('Open now') : t('December 1, 2026–February 14, 2027')}
              </p>
              <p className="ccsf-prelaunch__lede">
                {t('Join your residence community in taking practical actions that save energy and water, earn points, and build more sustainable habits.')}
              </p>
              <div className="ccsf-prelaunch__hero-ctas">
                {launched || needsJoin ? (
                  <Link to={primaryPath}>
                    <Button color="light" size="lg">{primaryLabel}</Button>
                  </Link>
                ) : (
                  <Button color="light" size="lg" onClick={this.scrollToSignup}>
                    {t('Pre-register')}
                  </Button>
                )}
                <Button
                  outline={true}
                  color="light"
                  size="lg"
                  onClick={this.scrollToHow}
                >
                  {t('See how it works')}
                </Button>
              </div>
            </div>
          </section>

          <section
            className="ccsf-prelaunch__section"
            id="ucd-ewc-signup"
            aria-labelledby="ucd-ewc-signup-heading"
          >
            <div className="ccsf-prelaunch__wrap ccsf-prelaunch__signup">
              {launched ? (
                <React.Fragment>
                  <h2 id="ucd-ewc-signup-heading">{t('Join the Challenge')}</h2>
                  <p>
                    {t('Create or log into your account with your UC Davis email, choose your residence community, and start taking actions.')}
                  </p>
                  <div className="ccsf-prelaunch__hero-ctas ucd-ewc-prelaunch__account-actions">
                    <Link to={primaryPath}>
                      <Button color="primary" size="lg">{primaryLabel}</Button>
                    </Link>
                    <Link to={loginPath}>
                      <Button outline={true} color="primary" size="lg">{t('Log in')}</Button>
                    </Link>
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <h2 id="ucd-ewc-signup-heading">{t('Pre-register for the 2027 Challenge')}</h2>
                  <p>
                    {t('Get a confirmation and calendar invite now, plus a reminder when the Challenge opens.')}
                  </p>
                  {success ? (
                    <div
                      className="ccsf-prelaunch__success"
                      role="status"
                      tabIndex={-1}
                      ref={(el) => { this.successRef = el; }}
                    >
                      <h3>{t('You’re pre-registered!')}</h3>
                      <p>{t('We’ll remind you when the Energy & Water Challenge opens.')}</p>
                      {ics && (
                        <Button color="primary" onClick={this.downloadIcs}>
                          {t('Download calendar invite (.ics)')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={this.submit} className="ccsf-prelaunch__form">
                      {error && (
                        <div
                          ref={(el) => { this.errorRef = el; }}
                          tabIndex={-1}
                        >
                          <Alert color="danger" id="ucd-ewc-form-error" role="alert">
                            {error}
                          </Alert>
                        </div>
                      )}
                      <div className="ccsf-prelaunch__form-row">
                        <FormGroup>
                          <Label for="ucd-ewc-first">{t('First name')}</Label>
                          <Input
                            id="ucd-ewc-first"
                            name="given-name"
                            autoComplete="given-name"
                            value={firstName}
                            required={true}
                            aria-invalid={Boolean(error)}
                            aria-describedby={error ? 'ucd-ewc-form-error' : undefined}
                            onChange={(event) => this.setState({ firstName: event.target.value })}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="ucd-ewc-last">{t('Last name')}</Label>
                          <Input
                            id="ucd-ewc-last"
                            name="family-name"
                            autoComplete="family-name"
                            value={lastName}
                            required={true}
                            onChange={(event) => this.setState({ lastName: event.target.value })}
                          />
                        </FormGroup>
                      </div>
                      <FormGroup>
                        <Label for="ucd-ewc-email">{t('UC Davis email')}</Label>
                        <Input
                          id="ucd-ewc-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          value={email}
                          required={true}
                          aria-describedby="ucd-ewc-email-hint"
                          onChange={(event) => this.setState({ email: event.target.value })}
                        />
                        <small className="ccsf-prelaunch__hint" id="ucd-ewc-email-hint">
                          {t('Use your @ucdavis.edu email address.')}
                        </small>
                      </FormGroup>
                      <FormGroup>
                        <Label for="ucd-ewc-team">{t('Residence community (optional)')}</Label>
                        <Input
                          id="ucd-ewc-team"
                          name="organization"
                          type="select"
                          value={residenceTeam}
                          onChange={(event) => this.setState({ residenceTeam: event.target.value })}
                        >
                          <option
                            value=""
                            aria-selected={residenceTeam === ''}
                          >
                            {t('I’m not sure yet')}
                          </option>
                          {residenceTeams.map((team) => (
                            <option
                              key={team.id}
                              value={team.name}
                              aria-selected={residenceTeam === team.name}
                            >
                              {team.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                      <FormGroup check={true}>
                        <CustomInput
                          type="checkbox"
                          id="ucd-ewc-updates"
                          checked={wantsUpdates}
                          onChange={(event) => this.setState({ wantsUpdates: event.target.checked })}
                          label={t('Send me Challenge updates')}
                        />
                      </FormGroup>
                      <Button
                        color="primary"
                        size="lg"
                        disabled={submitting}
                        type="submit"
                        aria-busy={submitting}
                      >
                        {submitting ? t('Submitting…') : t('Pre-register')}
                      </Button>
                    </form>
                  )}
                </React.Fragment>
              )}
            </div>
          </section>

          <section
            className="ccsf-prelaunch__section ccsf-prelaunch__section--tint"
            id="ucd-ewc-how"
            aria-labelledby="ucd-ewc-how-heading"
          >
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ucd-ewc-how-heading">{t('How it works')}</h2>
              <ol className="ccsf-prelaunch__steps">
                {[
                  ['1', 'Join your residence team', 'Create an account with your UC Davis email and select your community.'],
                  ['2', 'Choose actions', 'Pick energy- and water-saving actions that fit your daily routine.'],
                  ['3', 'Track your impact', 'Record completed actions to earn points and estimate savings.'],
                  ['4', 'Cheer on your team', 'Follow the scoreboard and help your residence community climb the rankings.']
                ].map((step) => (
                  <li className="ccsf-prelaunch__step" key={step[0]}>
                    <div className="ccsf-prelaunch__step-num" aria-hidden={true}>{step[0]}</div>
                    <h3>{t(step[1])}</h3>
                    <p>{t(step[2])}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="ccsf-prelaunch__section" aria-labelledby="ucd-ewc-details-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ucd-ewc-details-heading">{t('Challenge details')}</h2>
              <div className="ccsf-prelaunch__cards">
                {[
                  ['Opens', 'December 1, 2026'],
                  ['Ends', 'February 14, 2027'],
                  ['Who can join', 'UC Davis students and community members with an @ucdavis.edu email'],
                  ['Team format', 'Residence communities compete by completing energy- and water-saving actions']
                ].map((detail) => (
                  <div className="ccsf-prelaunch__card" key={detail[0]}>
                    <h3>{t(detail[0])}</h3>
                    <p>{t(detail[1])}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <footer className="ccsf-prelaunch__footer">
          <div className="ccsf-prelaunch__wrap ccsf-prelaunch__footer-inner">
            <div>
              <strong>{t('UC Davis Energy & Water Challenge 2027')}</strong>
              <p>{t('Student Housing and Dining Services')}</p>
            </div>
            <a
              className="ccsf-prelaunch__powered"
              href="https://coolclimate.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              Powered by CoolClimate Network
            </a>
            <nav className="ccsf-prelaunch__footer-links" aria-label={t('Footer')}>
              <Link to={makeSimpleRoutePath(Routes.Accessibility, t)}>Accessibility</Link>
              <Link to={loginPath}>Account login</Link>
            </nav>
          </div>
        </footer>
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => {
  const competition = state.competition;
  const residenceTeams = values(state.entities || {})
    .filter((entity) => entity && entity.type === Entity.TypeEnum.Team)
    .filter((entity) => !isHiddenFromScoreboard(entity, competition))
    .sort((left, right) => left.name.localeCompare(right.name));

  return {
    competition,
    residenceTeams,
    apiBase: userApiBasePath(state)
  };
};

export default compose(
  withLocalization('ucdEwcPrelaunch'),
  connect(mapStateToProps)
)(UcdEwcPrelaunchLanding) as any;
